import { useEffect, useMemo, useState } from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActionArea from '@material-ui/core/CardActionArea';
import CardContent from '@material-ui/core/CardContent';
import CircularProgress from '@material-ui/core/CircularProgress';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormHelperText from '@material-ui/core/FormHelperText';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Radio from '@material-ui/core/Radio';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import DesktopWindowsIcon from '@material-ui/icons/DesktopWindows';
import { createFormField } from '@backstage/plugin-scaffolder-react/alpha';
import { useActionToast } from '../../../modules/actionToast';

export type OciImage = {
  id: string;
  displayName: string;
  operatingSystem: string;
  operatingSystemVersion: string;
};

export function OciImagePickerComponent({
  onChange,
  formData,
  formContext,
}: any) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const { notify } = useActionToast();
  const fd = (formContext as any)?.formData ?? {};
  const region: string = fd.region ?? '';
  const compartmentId: string = fd.compartmentId ?? '';
  const [images, setImages] = useState<OciImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [family, setFamily] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string>('');
  const [importMode, setImportMode] = useState(false);
  const [importOcid, setImportOcid] = useState('');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');

  useEffect(() => {
    if (!region || !compartmentId) {
      setImages([]);
      onChange(undefined as any);
      return;
    }
    setLoading(true);
    setError('');
    discoveryApi.getBaseUrl('infrastructure').then(baseUrl => {
      fetchApi
        .fetch(
          `${baseUrl}/oci/images?region=${encodeURIComponent(
            region,
          )}&compartmentId=${encodeURIComponent(compartmentId)}`,
        )
        .then(r => r.json())
        .then(data => {
          if (data.error) {
            setError(
              typeof data.error === 'string'
                ? data.error
                : data.error?.message ?? 'Failed to load images',
            );
          } else {
            setImages(data.images ?? []);
          }
        })
        .catch(() => setError('Failed to load images'))
        .finally(() => setLoading(false));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, compartmentId]);

  const families = useMemo(
    () => Array.from(new Set(images.map(i => i.operatingSystem))).sort(),
    [images],
  );

  const imagesInFamily = useMemo(
    () => images.filter(i => i.operatingSystem === family),
    [images, family],
  );

  if (!region || !compartmentId) {
    return (
      <FormHelperText>Select a region and compartment first</FormHelperText>
    );
  }

  if (loading) return <CircularProgress size={20} />;

  // Neither of these early-return without a way in any more — an errored or
  // empty discovery fetch (see the "Custom" images not always showing up
  // for a given region/compartment) is exactly when Import is most needed,
  // so both states still render the trigger button + dialog below rather
  // than dead-ending the field.
  let discoveryMessage: string | null = null;
  if (error) {
    discoveryMessage = error;
  } else if (images.length === 0) {
    discoveryMessage =
      'No images discovered in this compartment — use Import below if you have the image OCID.';
  }

  const openDialog = () => {
    setFamily(null);
    setPendingId(formData?.id ?? '');
    setImportMode(false);
    setImportOcid('');
    setImportError('');
    setOpen(true);
  };

  const confirm = () => {
    const image = images.find(i => i.id === pendingId);
    if (image) onChange(image);
    setOpen(false);
  };

  const submitImport = () => {
    const imageId = importOcid.trim();
    if (!imageId) return;
    setImporting(true);
    setImportError('');
    discoveryApi.getBaseUrl('infrastructure').then(baseUrl => {
      fetchApi
        .fetch(`${baseUrl}/oci/images/import`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ region, compartmentId, imageId }),
        })
        .then(r => r.json().then(body => ({ ok: r.ok, body })))
        .then(({ ok, body }) => {
          if (!ok) {
            setImportError(body.error ?? 'Failed to import image');
            return;
          }
          setImages(prev => [
            body.image,
            ...prev.filter(i => i.id !== body.image.id),
          ]);
          setPendingId(body.image.id);
          setImportMode(false);
          setImportOcid('');
          setFamily('Custom');
          notify('image-imported', {
            title: 'Image imported',
            description: body.image.displayName,
            timeout: 4000,
          });
        })
        .catch(() => setImportError('Failed to import image'))
        .finally(() => setImporting(false));
    });
  };

  return (
    <Box>
      {formData ? (
        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
          <Typography>
            <strong>{formData.displayName}</strong> ({formData.operatingSystem}{' '}
            {formData.operatingSystemVersion})
          </Typography>
          <Button size="small" variant="outlined" onClick={openDialog}>
            Change Image
          </Button>
        </Box>
      ) : (
        <Button variant="outlined" color="primary" onClick={openDialog}>
          Select Image
        </Button>
      )}
      {discoveryMessage && (
        <FormHelperText error={!!error} style={{ marginTop: 4 }}>
          {discoveryMessage}
        </FormHelperText>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {importMode && (
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <IconButton
                size="small"
                onClick={() => {
                  setImportMode(false);
                  setImportError('');
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              Import Custom Image
            </Box>
          )}
          {!importMode && !!family && (
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <IconButton size="small" onClick={() => setFamily(null)}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              {family}
            </Box>
          )}
          {!importMode && !family && 'Select an Image'}
        </DialogTitle>
        <DialogContent>
          {importMode && (
            <Box style={{ paddingTop: 4 }}>
              <Typography variant="body2" color="textSecondary" paragraph>
                Not showing up in the list above? Paste its OCID directly —
                it'll be added to the Custom family for this region and
                compartment.
              </Typography>
              <TextField
                fullWidth
                label="OCI Image OCID"
                placeholder="ocid1.image.oc1...."
                value={importOcid}
                onChange={e => setImportOcid(e.target.value)}
                error={!!importError}
                helperText={importError || ' '}
                disabled={importing}
              />
            </Box>
          )}
          {!importMode && !family && (
            <Grid container spacing={2}>
              {families.map(f => (
                <Grid item xs={6} sm={4} md={3} key={f}>
                  <Card variant="outlined">
                    <CardActionArea onClick={() => setFamily(f)}>
                      <CardContent style={{ textAlign: 'center' }}>
                        <DesktopWindowsIcon fontSize="large" />
                        <Typography variant="body2">{f}</Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
          {!importMode && !!family && (
            <List>
              {imagesInFamily.map(image => (
                <ListItem
                  key={image.id}
                  button
                  onClick={() => setPendingId(image.id)}
                >
                  <Radio checked={pendingId === image.id} size="small" />
                  <ListItemText
                    primary={image.displayName}
                    secondary={image.operatingSystemVersion}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          {importMode ? (
            <>
              <Button
                onClick={() => {
                  setImportMode(false);
                  setImportError('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!importOcid.trim() || importing}
                onClick={submitImport}
              >
                {importing ? 'Importing…' : 'Import'}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              {family === 'Custom' && (
                <Button onClick={() => setImportMode(true)}>Import</Button>
              )}
              <Button
                variant="contained"
                color="primary"
                disabled={!pendingId}
                onClick={confirm}
              >
                Select Image
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export const OciImagePickerField = createFormField({
  name: 'OciImagePicker',
  component: OciImagePickerComponent,
});
